<template>
  <div class="register-wrapper d-flex align-items-center justify-content-center">
    <div class="register-card card shadow p-4">
      <div class="mb-3">
        <button class="btn btn-outline-secondary btn-sm" @click="$router.push('/bienvenida')">
          <font-awesome-icon icon="arrow-left" class="me-2" />
          Volver al inicio
        </button>
      </div>

      <div class="text-center mb-4">
        <h2 class="fw-bold">Crear Cuenta</h2>
        <p class="text-muted">Registrate para empezar a usar Araucann</p>
      </div>

      <div v-if="errorMessage" class="alert alert-danger d-flex align-items-center gap-2" role="alert">
        <font-awesome-icon icon="exclamation-triangle" />
        <span class="flex-grow-1">{{ errorMessage }}</span>
        <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Cerrar"></button>
      </div>

      <form @submit.prevent="register">
        <!-- Datos de usuario -->
        <div class="mb-3">
          <label for="username" class="form-label">Nombre de Usuario</label>
          <input type="text" id="username" v-model="username" class="form-control" placeholder="Juan Pérez" required />
        </div>

        <div class="mb-3">
          <label for="email" class="form-label">Correo Electrónico</label>
          <input type="email" id="email" v-model="email" class="form-control" placeholder="tucorreo@ejemplo.com"
            required />
        </div>

        <div class="mb-3">
          <label for="password" class="form-label">Contraseña</label>
          <div class="position-relative">
            <input :type="mostrarPassword ? 'text' : 'password'" id="password" v-model="password"
              class="form-control pe-5" placeholder="********" required />
            <button type="button"
              class="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-2"
              @click="mostrarPassword = !mostrarPassword" tabindex="-1">
              <font-awesome-icon :icon="['fas', mostrarPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <small class="text-muted">Mín. 8 caracteres, con mayúscula, minúscula y número.</small>
        </div>

        <div class="mb-3">
          <label for="repeatPassword" class="form-label">Repetir Contraseña</label>
          <div class="position-relative">
            <input :type="mostrarPassword ? 'text' : 'password'" id="repeatPassword" v-model="repeatPassword"
              class="form-control pe-5" placeholder="********" required />
            <button type="button"
              class="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-2"
              @click="mostrarPassword = !mostrarPassword" tabindex="-1">
              <font-awesome-icon :icon="['fas', mostrarPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
        </div>

        <!-- Checkbox + Botón para mostrar los términos -->
        <div class="form-check mb-3">
          <input class="form-check-input" type="checkbox" id="aceptaTerminos" v-model="aceptaTerminos" required />
          <label class="form-check-label" for="aceptaTerminos">
            He leído y acepto los
            <button type="button" class="btn btn-link p-0 m-0 align-baseline fw-semibold"
              @click="mostrarTerminos = !mostrarTerminos">
              Términos y Condiciones
            </button>
          </label>
        </div>

        <!-- Caja de Términos (toggle visible) -->
        <div v-if="mostrarTerminos" class="mb-3">
          <div class="border rounded p-3 bg-light small terms-box">
            <p><strong>Última actualización:</strong> julio 2025</p>

            <p><strong>1. Identificación:</strong> Araucann es un sistema de gestión, trazabilidad y planificación para
              clubes de cultivo, proyectos de cáñamo o cannabis y organizaciones relacionadas.</p>

            <p><strong>2. Aceptación del usuario:</strong> El uso de Araucann implica la aceptación plena y sin reservas
              de estos Términos. El usuario declara ser mayor de edad y tener capacidad legal para contratar.</p>

            <p><strong>3. El sistema debe usarse dentro del marco legal aplicable. No está permitido utilizarlo para
                actividades que violen la ley o regulaciones vigentes. </strong></p>

            <p><strong>4. Propiedad intelectual:</strong> El contenido que carguen los usuarios les pertenece, pero
              Araucann puede almacenarlo y procesarlo para el funcionamiento del sistema. El usuario obtiene una
              licencia de uso limitada y revocable. Está prohibida su reproducción o modificación sin autorización.</p>

            <p><strong>5. Registro y seguridad:</strong> El usuario es responsable de sus credenciales. No nos
              responsabilizamos por accesos indebidos causados por negligencia del usuario.</p>

            <p><strong>6. Limitación de responsabilidad:</strong> Araucann no garantiza la disponibilidad continua del
              servicio, ni se hace responsable por errores, interrupciones o decisiones basadas en datos cargados por el
              usuario. Las decisiones tomadas en base a la información del sistema son responsabilidad exclusiva del
              usuario.</p>

            <p><strong>7. Datos personales:</strong> Serán tratados de manera confidencial y no se compartirán sin
              consentimiento, salvo requerimiento legal. El usuario puede solicitar su eliminación en cualquier momento.
              Los datos se tratarán conforme a la Ley 25.326 de Protección de Datos Personales.
            </p>

            <p><strong>8. Modificaciones:</strong> Araucann puede modificar estos Términos y notificará los cambios
              mediante la app o por correo. El uso continuado implica aceptación de las nuevas condiciones. La versión
              actualizada estará siempre disponible dentro del sistema.</p>

            <p><strong>9. Terminación del servicio:</strong> Araucann puede suspender cuentas por mal uso o razones
              técnicas/comerciales. El usuario puede cancelar su cuenta en cualquier momento desde su perfil.</p>

            <p><strong>10. Legislación aplicable:</strong> Estos términos se rigen por las leyes de la República
              Argentina. Jurisdicción: Neuquén.</p>

            <p><strong>11. Contacto:</strong> Para consultas o soporte, escribinos a: <a
                href="araucannsoftware@gmail.com">araucannsoftware@gmail.com</a></p>
            <p><strong>12. Planes y precios:</strong> El plan actual tiene un costo mensual, con las funciones incluidas
              al momento de contratación. Araucann se reserva el derecho a modificar los precios o lanzar nuevos planes
              con funcionalidades adicionales. El precio actual no garantiza acceso futuro a nuevas funciones si estas
              son parte de planes avanzados.</p>
            <p><strong>13. Limitaciones del plan:</strong> El plan base puede tener límites funcionales, como cantidad
              de usuarios internos, proyectos activos, o configuración personalizada. Estos límites pueden ser
              modificados según evolución del sistema y se comunicarán dentro de la plataforma.</p>

            <p><strong>14. Uso adecuado:</strong> El sistema debe ser utilizado exclusivamente para la gestión y
              trazabilidad de cultivos legales. Araucann no se responsabiliza por el contenido ingresado por los
              usuarios ni por el uso indebido del sistema fuera de su propósito previsto.</p>




            <p class="text-center mt-3"><em>Gracias por usar Araucann 🌱</em></p>
          </div>
        </div>




        <button type="submit" class="btn btn-success w-100" :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Registrarse
        </button>
      </form>

      <div class="text-center mt-3">
        <p class="text-muted">¿Ya tenés una cuenta?
          <router-link to="/login" class="fw-bold">Iniciar sesión</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'UserRegister',
  data() {
    return {
      username: '',
      email: '',
      password: '',
      repeatPassword: '',
      mostrarPassword: false,
      aceptaTerminos: false,
      errorMessage: '',
      mostrarTerminos: false,
      isLoading: false,
    };
  },
  methods: {
    validarPassword(password) {
      const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
      return regex.test(password);
    },
    async register() {
      this.errorMessage = '';
      this.isLoading = true;

      if (this.password !== this.repeatPassword) {
        this.errorMessage = 'Las contraseñas no coinciden.';
        this.isLoading = false;
        return;
      }

      if (!this.validarPassword(this.password)) {
        this.errorMessage = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número.';
        this.isLoading = false;
        return;
      }

      if (!this.aceptaTerminos) {
        this.errorMessage = 'Debés aceptar los Términos y Condiciones para registrarte.';
        this.isLoading = false;
        return;
      }

      try {
        await axios.post('/api/auth/usuarios/', {
          nombre: this.username,
          email: this.email,
          password: this.password,
          acepto_terminos: this.aceptaTerminos,
        }, { withCredentials: true });

        alert('Registro exitoso. Ahora podés iniciar sesión.');
        this.$router.push('/login');
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Error en el registro.';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>


<style scoped>
.terms-box {
  max-height: 200px;
  overflow-y: auto;
  font-size: 0.875rem;
  background-color: #f9f9f9;
  line-height: 1.5;
  transition: all 0.3s ease-in-out;
}
</style>
